import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearch } from '@tanstack/react-router'
import { getJobIdFromStoredJwt } from '@/api/services/auth.service'
import { getEmbedParentOrigins } from '@/config/config'
import type { EmbedSession } from '@/contexts/embed-session-context'
import { type EmbedErrorCode, jobMatchesHost, larryErrorMessage } from '@/lib/embed/host-messages'
import { useAuth } from '@/providers/auth'
import { EmbedStatus } from './components/embed-status'
import { EmbeddedEditor } from './components/embedded-editor'
import { HOST_INIT_TIMEOUT_MS } from './constants/embed-timing'
import { postToParent } from './handlers/post-to-parent'
import { useHostHandshake } from './hooks/use-host-handshake'
import { useHostSessionEnded } from './hooks/use-host-session-ended'

type EmbedFailure = { code: EmbedErrorCode; message: string }

/**
 * `/embed?spreadId=…` — Larry opened inside a host app's iframe (Mary's spread ladder). Skips the
 * login screen: posts `ready`, takes the session token from the host's `init`, signs in with it,
 * checks the session's job matches the host's, then opens the editor locked to the host's spread.
 * Any failure is shown here and reported to the host as an `error` message. If the session later
 * ends (force logout), the host is told with `session-expired` instead of Larry showing its login.
 */
export default function Embed() {
  const { spreadId } = useSearch({ from: '/embed' })
  const allowedOrigins = useMemo(() => getEmbedParentOrigins(), [])
  const { adoptSession } = useAuth()

  const [failure, setFailure] = useState<EmbedFailure | null>(() =>
    spreadId
      ? null
      : { code: 'missing-spread-id', message: 'No spread was given to open in the editor.' }
  )
  const [sessionAdopted, setSessionAdopted] = useState(false)
  const handshake = useHostHandshake(allowedOrigins, HOST_INIT_TIMEOUT_MS, failure === null)

  const parentOrigin = handshake.status === 'connected' ? handshake.parentOrigin : null
  const handshakeFailure =
    handshake.status === 'failed' ? { code: handshake.code, message: handshake.message } : null
  const activeFailure = failure ?? handshakeFailure
  const hostOrigins = useMemo(
    () => (parentOrigin ? [parentOrigin] : allowedOrigins),
    [allowedOrigins, parentOrigin]
  )

  const fail = useCallback((code: EmbedErrorCode, message: string) => {
    setFailure((current) => current ?? { code, message })
  }, [])

  // Adopt the host's session once its `init` arrives.
  const adoptStartedRef = useRef(false)
  const isHostSessionStarted = useCallback(() => adoptStartedRef.current, [])
  const sessionEnded = useHostSessionEnded(hostOrigins, isHostSessionStarted)

  useEffect(() => {
    if (handshake.status !== 'connected' || adoptStartedRef.current) return
    adoptStartedRef.current = true

    const { wpcauth, jobId, userName } = handshake.init

    void (async () => {
      const adopted = await adoptSession(wpcauth, userName ?? '')

      if (!adopted) {
        fail('session-rejected', 'Your session could not be used to open the editor.')
        return
      }

      if (!jobMatchesHost(getJobIdFromStoredJwt(), jobId)) {
        fail('job-mismatch', 'This session belongs to a different book than the one open.')
        return
      }

      setSessionAdopted(true)
    })()
  }, [adoptSession, fail, handshake])

  // Report a failure to the host once: at its origin when known, else to every allowed origin.
  const reportedFailureRef = useRef<EmbedFailure | null>(null)

  useEffect(() => {
    if (!activeFailure || reportedFailureRef.current) return
    reportedFailureRef.current = activeFailure
    postToParent(larryErrorMessage(activeFailure.code, activeFailure.message), hostOrigins)
  }, [activeFailure, hostOrigins])

  const session = useMemo<EmbedSession | null>(
    () =>
      parentOrigin && spreadId
        ? { spreadId, postToHost: (message) => postToParent(message, [parentOrigin]) }
        : null,
    [parentOrigin, spreadId]
  )

  if (sessionEnded) {
    return <EmbedStatus message="Your session has ended. Sign in again to keep editing." isError />
  }

  if (activeFailure) return <EmbedStatus message={activeFailure.message} isError />
  if (!session || !sessionAdopted) return <EmbedStatus message="Opening the editor…" />

  return <EmbeddedEditor session={session} onFailure={fail} />
}
